import { memo, type ReactNode, type MouseEvent } from "react";
import { useTranslation } from "react-i18next";
import CustomModal from "sipli-fleet-core/components/CustomSharedModal/CustomSharedModal";
import CustomTypography from "sipli-fleet-core/components/CustomSharedTypography/CustomSharedTypography";
import CustomButton from "sipli-fleet-core/components/CustomSharedButton/CustomSharedButton";
import DELETE_CONFIRM_ICON from "sipli-fleet-core/assets/icons/trash-animated.svg?url";

const { CustomText, CustomParagraph } = CustomTypography;

export interface CustomConfirmationModalProps {
  open: boolean;
  onCancel?: (event?: MouseEvent) => void;
  onConfirm?: (event?: MouseEvent) => void;
  title: ReactNode;
  description: ReactNode;
  confirmText?: string;
  cancelText?: string;
  isLoading?: boolean;
  danger?: boolean;
  centered?: boolean;
  alsoDeleteCheckbox?: ReactNode;
  width?: number;
  zIndex?: number;
}

function CustomConfirmationModal({
  open,
  onCancel,
  onConfirm,
  title,
  description,
  confirmText,
  cancelText,
  isLoading = false,
  danger = true,
  centered,
  alsoDeleteCheckbox,
  width = 480,
  zIndex,
}: CustomConfirmationModalProps) {
  const { t } = useTranslation();

  const stopRowClickPropagation = (event?: MouseEvent) => {
    event?.preventDefault?.();
    event?.stopPropagation?.();
  };

  const handleCancel = (event?: MouseEvent) => {
    stopRowClickPropagation(event);
    onCancel?.(event);
  };

  const handleConfirm = (event?: MouseEvent) => {
    stopRowClickPropagation(event);
    onConfirm?.(event);
  };

  return (
    <CustomModal
      open={open}
      onCancel={handleCancel}
      showFooter={false}
      width={width}
      centered={centered}
      zIndex={zIndex}
    >
      <div className="flex flex-col items-center text-center" onClick={stopRowClickPropagation}>
        <img src={DELETE_CONFIRM_ICON} alt="delete" className="mb-4" />

        <CustomText className="!font-bold" style={{ fontSize: 20, lineHeight: "28px" }}>
          {title}
        </CustomText>

        <CustomParagraph className="!text-gray8 mt-2 mb-6" style={{ fontSize: 14 }}>
          {description}
        </CustomParagraph>

        {alsoDeleteCheckbox}

        <div className="flex w-full gap-3 mt-6">
          <CustomButton
            type="secondary"
            label={cancelText || t("cancel")}
            className="flex-1"
            onClick={handleCancel}
            disabled={isLoading}
          />

          <CustomButton
            type="primary"
            danger={danger}
            label={confirmText || t("delete")}
            className="flex-1"
            onClick={handleConfirm}
            loading={isLoading}
          />
        </div>
      </div>
    </CustomModal>
  );
}

export default memo(CustomConfirmationModal);
